import { useEffect, useState } from 'react'
import { api, type ArmPerformance, type ExperimentsPayload } from '../api'

/**
 * Improvement — the running experiments and how their arms are doing.
 *
 * ONE copy system. Fragments live in sections.yaml; alternative copy is a
 * `variant` on a fragment, activated by an experiment arm. Milestones used to
 * be here and are gone (D94): a moment worth saying something at is a campaign.
 *
 * One honest limit is stated on the screen rather than discovered as
 * confusion: performance is EMPTY until real sending happens. Zeros are not
 * results.
 *
 * And one thing that is deliberately NOT here: a promote button. Which arm an
 * account gets is deterministic from its id, not chosen; which copy wins is a
 * person reading the numbers and editing the fragments in git, where the
 * change gets reviewed.
 */
export function ImprovementTab() {
  const [experiments, setExperiments] = useState<ExperimentsPayload | null>(null)
  const [perf, setPerf] = useState<ArmPerformance | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [e, p] = await Promise.all([api.experiments(), api.armPerformance()])
        setExperiments(e)
        setPerf(p)
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  if (error) return <div className="error">{error}</div>
  if (!experiments || !perf) return <div className="loading">Loading…</div>

  return (
    <>
      {!perf.measurable && (
        <div className="panel">
          <h2>Nothing measured yet</h2>
          <div className="panel-body">
            <p className="hint">
              {perf.why_empty}
            </p>
            <p className="hint">
              Any number below is zero because no arm has had the chance to perform — not because
              it performed badly. Choosing between arms now would be a coin toss wearing a chart.
            </p>
          </div>
        </div>
      )}

      <div className="panel">
        <h2>
          Experiments <span className="count">{experiments.experiments.length} declared</span>
        </h2>
        <div className="panel-body">
          <p className="hint">
            Declared in <span className="mono">experiments.yaml</span> — arms, hypothesis and
            metric written down together, in git. An account is assigned to an arm{' '}
            <strong>deterministically from its id</strong>, so nothing is stored and anyone can
            recompute months later which arm an account was in. That is what makes a result
            auditable rather than asserted. One experiment claims an account at most: two claiming
            the same person makes both unreadable.
          </p>
          {experiments.holdout.enabled && (
            <p className="hint">
              A <strong>{(experiments.holdout.share ?? 0) * 100}% holdout</strong> sits outside
              every experiment and is never mailed at all — {experiments.holdout.note}
            </p>
          )}
        </div>

        <table>
          <thead>
            <tr>
              <th>Experiment</th>
              <th>Arms</th>
              <th>Slots it changes</th>
              <th>Metric</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {experiments.experiments.map((e) => (
              <tr key={e.id}>
                <td>
                  <span className="mono">{e.id}</span>
                  <div className="hint caption">
                    {e.title}
                  </div>
                </td>
                <td className="mono">{e.arms.join(' vs ')}</td>
                <td className="mono">{e.slots.join(', ') || '—'}</td>
                <td className="mono">{e.metric ?? '—'}</td>
                <td>
                  {e.enabled ? (
                    <span className="badge ok">running</span>
                  ) : (
                    <span className="badge catchall">off</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h2>How the arms are doing</h2>
        {perf.rows.length === 0 ? (
          <div className="empty">No sends recorded, so there is nothing to compare.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Experiment</th>
                <th>Arm</th>
                <th className="num">Sent</th>
                <th className="num">Delivered</th>
                <th className="num">Opened</th>
                <th className="num">Clicked</th>
              </tr>
            </thead>
            <tbody>
              {perf.rows.map((r) => (
                <tr key={`${r.experiment}:${r.arm}`}>
                  <td className="mono">{r.experiment}</td>
                  <td className="mono">{r.arm}</td>
                  <td className="num">{r.sent}</td>
                  <td className="num">{r.delivered}</td>
                  <td className="num">{r.opened}</td>
                  <td className="num">{r.clicked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </>
  )
}
